import React, { ReactNode, useEffect, useState } from "react";
import ToggleIcon from "../../Components/ToggleIcon";
import { IScene } from "./types";
import useModeColor, { TMode } from "../../hooks/useModeColor";
import { useSocketClient } from "../../contexts/SocketClientProvider";
import { useSceneAttribute } from "../../hooks/useSceneAttribute";
import { useUser } from "../../contexts/UserProvider";
import useDeleteData from "../../hooks/useDeleteData";
import { Grid, Paper, Stack, Typography } from "@mui/material";
import ToggleIconLink from "../../Components/ToggleIconLink";

type TSceneListItemProps = {
  scene: IScene;
};

export const SceneListClickItem = (props: TSceneListItemProps) => {
  const { scene } = props;
  const status = useSceneAttribute(scene, "status");
  const [mode, setMode] = useState<TMode>(
    scene.is_enabled ? "off" : "disabled",
  );
  const iconName =
    mode === "on" ? "stop-circle-outline" : "play-circle-outline";
  const iconColor = useModeColor(mode);
  const socketClient = useSocketClient();
  const toggleStatus = () => {
    if (scene.is_enabled) {
      setMode("on");
      socketClient.process_command({
        type: "scene",
        id: scene.id,
        command: "toggle",
        value: status,
      });
    }
  };

  useEffect(() => {
    if (mode === "on") {
      const timer = setTimeout(() => setMode("off"), 300);
      return () => clearTimeout(timer);
    }
  }, [mode]);

  return (
    <ScenePanel id={scene.id} mode={mode} title={scene.name}>
      <ToggleIcon name={iconName} color={iconColor} onClick={toggleStatus} />
    </ScenePanel>
  );
};

export const SceneListToggleItem = (props: TSceneListItemProps) => {
  const { scene } = props;
  const status = useSceneAttribute(scene, "status");
  const mode: TMode = scene.is_enabled ? status : "disabled";
  const iconName =
    status === "on" ? "play-circle-outline" : "stop-circle-outline";
  const iconColor = useModeColor(mode);
  const socketClient = useSocketClient();
  const toggleStatus = () => {
    if (scene.is_enabled) {
      socketClient.process_command({
        type: "scene",
        id: scene.id,
        command: "toggle",
        value: status === "on" ? "off" : "on",
      });
    }
  };

  return (
    <ScenePanel id={scene.id} mode={mode} title={scene.name}>
      <ToggleIcon name={iconName} color={iconColor} onClick={toggleStatus} />
    </ScenePanel>
  );
};

type TScenePanelProps = {
  id: number;
  mode: TMode;
  title: string;
  children?: ReactNode;
};

const ScenePanel = (props: TScenePanelProps) => {
  const { id, mode, title, children } = props;
  const { user } = useUser();
  const deleteHandler = useDeleteData(`scene "${title}"`, `/scenes/${id}`);

  return (
    <Grid size={4} sx={{ textAlign: "center" }}>
      <Paper
        sx={{ bgcolor: `${mode}.main`, color: `${mode}.text` }}
        data-cy={`scene-${id}`}
      >
        <Typography sx={{ fontSize: 14 }} data-cy="scene-name">
          {title}
        </Typography>
        <Stack
          direction="row"
          justifyContent="space-evenly"
          padding={0.5}
          data-cy="scene-buttons"
        >
          {children}
          {user?.is_admin && (
            <>
              <ToggleIcon
                name={"delete"}
                onClick={deleteHandler}
                data-cy="scene-delete"
              />
              <ToggleIconLink
                to={`/scenes/${id}`}
                name={"pencil"}
                data-cy="scene-edit"
              />
            </>
          )}
        </Stack>
        <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>
          <span>&nbsp;</span>
        </Typography>
      </Paper>
    </Grid>
  );
};
